import React, {
  useEffect, useRef, useState,
} from 'react';
import clsx from 'clsx';
import { usePopper } from 'react-popper';
import PropTypes from 'prop-types';
import './dropdown.scss';
import { CSSTransition } from 'react-transition-group';
import DropdownItem, { DropdownTypes, DropdownSeparatorId } from './dropdown-item';
import useStateRef from '../../enhancers/custom-hook/use-state-ref';
import { findAncestorMatch } from '../../../utils/general-utils';
import DropdownContent from './dropdown-content';
import {
  DEFAULT_OFFSET, hoverShowEvents, DropdownPlacements, hoverHideEvents, TransitionDuration,
  DropdownTriggers, PopperMinWidth, EventTypes, X_OFFSET, Y_OFFSET,
} from './constants';

/* eslint-disable no-underscore-dangle */
const Dropdown = ({
  defaultOpen, items, trigger, placement, children, enterDelay, onItemClick, onClose, closeOnClick,
  avatarView, withSearch, withClearSelection, onSelectionClear, groupOptionsOnCategory, categoryKey,
  footerActions, closeOnSelfClick, renderCustomView, enableCustomView, header,
}) => {
  const enterTimer = useRef();
  const leaveTimer = useRef();
  const popperInstance = useRef(null);
  const [offset, setOffset] = useState(DEFAULT_OFFSET);
  const [open, setOpen, openRef] = useStateRef(defaultOpen);
  const [popperElement, setPopperElement] = useState(null);
  const [referenceElement, setReferenceElement] = useState(null);
  const dropdownClass = clsx(
    'dropdown-popper',
    trigger,
    { 'no-items': items.length === 0 },
    { 'dropdown-open': open },
  );

  popperInstance.current = usePopper(referenceElement, popperElement, {
    modifiers: [
      {
        name: 'offset',
        options: {
          offset,
        },
      },
      {
        name: 'preventOverflow',
        options: {
          altBoundary: document.querySelector('#root'),
        },
      },
    ],
    placement,
    strategy: 'fixed',
  });

  const handleOpen = () => {
    setOpen(true);
    // setFocusedIndex(0); // Removed to fix the auto focus issue for tag input,
    // arrow navigation still works just that down-arrow navigation now starts from the first item
    // popperElement.setAttribute('data-show', '');
    popperInstance.current.update();
  };

  const handleClose = () => {
    setOpen(false);
    // popperElement.removeAttribute('data-show');
    if (onClose && typeof onClose === 'function') {
      onClose.call(null);
    }
  };

  const handleEnter = (event) => {
    clearTimeout(enterTimer.current);
    clearTimeout(leaveTimer.current);

    if (enterDelay) {
      enterTimer.current = setTimeout(
        () => {
          handleOpen(event);
        },
        enterDelay,
      );
    } else {
      handleOpen(event);
    }
  };

  const handleLeave = (event) => {
    clearTimeout(enterTimer.current);
    clearTimeout(leaveTimer.current);
    handleClose(event);
  };

  const handleReferenceRightClicked = (event) => {
    const { offsetX, offsetY, target } = event;
    const { offsetHeight } = target;
    handleOpen();
    setOffset([offsetX + X_OFFSET, offsetY - offsetHeight + Y_OFFSET]);
    event.preventDefault();
  };

  const handleReferenceClicked = () => {
    if (!openRef.current) {
      handleOpen();
    } else if (closeOnSelfClick) {
      handleClose();
    }
  };
  const handlePopperClicked = () => {};
  const handleDocumentClick = (event) => {
    if (
      event.target !== referenceElement
      && event.target !== popperElement
      && !findAncestorMatch(event.target, referenceElement)
      && !findAncestorMatch(event.target, popperElement)
    ) {
      handleClose();
      setOffset(DEFAULT_OFFSET);
    }
  };

  useEffect(() => {
    if (referenceElement && popperElement) {
      if (trigger === DropdownTriggers.HOVER) {
        hoverShowEvents.forEach((event) => {
          referenceElement.addEventListener(event, handleEnter);
        });

        hoverHideEvents.forEach((event) => {
          referenceElement.addEventListener(event, handleLeave);
        });
      } else if (trigger === DropdownTriggers.CLICK) {
        referenceElement.addEventListener(EventTypes.CLICK, handleReferenceClicked);
        document.addEventListener(EventTypes.CLICK, handleDocumentClick);
      } else if (trigger === DropdownTriggers.RIGHT_CLICK) {
        // Close popper on clicking outside or clicking on the children
        document.addEventListener(EventTypes.CLICK, handleDocumentClick);
        if (closeOnSelfClick) {
          referenceElement.addEventListener(EventTypes.CLICK, handleClose);
        }
        // Event listener for right click
        referenceElement.addEventListener(EventTypes.RIGHT_CLICK, handleReferenceRightClicked);
        document.addEventListener(EventTypes.RIGHT_CLICK, handleDocumentClick);
      }
    }
    return () => {
      if (referenceElement) {
        hoverShowEvents.forEach((event) => {
          referenceElement.removeEventListener(event, handleEnter);
        });

        hoverHideEvents.forEach((event) => {
          referenceElement.removeEventListener(event, handleLeave);
        });

        referenceElement.removeEventListener(EventTypes.CLICK, handleReferenceClicked);
        referenceElement.removeEventListener(EventTypes.RIGHT_CLICK, handleReferenceRightClicked);
      }
      document.removeEventListener(EventTypes.CLICK, handleDocumentClick);
    };
  }, [referenceElement, popperElement]);

  const childrenProps = {
    ...children.props,
    dropdownOpen: open,
    ref: setReferenceElement,
  };

  const repositionPopper = () => {
    setTimeout(() => {
      popperInstance.current.update();
    }, 1); // to reposition the dropdown
  };

  const getPopperMinWidth = () => Math.max(
    PopperMinWidth,
    referenceElement ? referenceElement.clientWidth : 0,
  );

  const getPopperElement = () => (
    <CSSTransition
      in={open}
      classNames="tp-trans"
      timeout={TransitionDuration}
      nodeRef={{ current: popperElement }}
    >
      <div
        className={dropdownClass}
        ref={setPopperElement}
        style={{ ...popperInstance.current.styles.popper, minWidth: getPopperMinWidth() }}
        data-show={open}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...popperInstance.current.attributes.popper}
        onClick={handlePopperClicked}
      >
        <DropdownContent
          items={items}
          header={header}
          closeOnClick={closeOnClick}
          onItemClick={onItemClick}
          avatarView={avatarView}
          withSearch={withSearch}
          withClearSelection={withClearSelection}
          onSelectionClear={onSelectionClear}
          groupOptionsOnCategory={groupOptionsOnCategory}
          categoryKey={categoryKey}
          footerActions={footerActions}
          renderCustomView={renderCustomView}
          enableCustomView={enableCustomView}
          handleClose={handleClose}
          repositionPopper={repositionPopper}
          open={open}
        />

      </div>
    </CSSTransition>
  );

  return (
    <>
      {React.cloneElement(children, childrenProps)}
      {getPopperElement()}
    </>
  );
};

Dropdown.defaultProps = {
  enterDelay: 100,
  defaultOpen: false,
  onClose: () => {},
  placeholderIcon: null,
  closeOnSelfClick: true,
  trigger: DropdownTriggers.CLICK,
  placement: DropdownPlacements.BOTTOM,
  ...DropdownContent.defaultProps,
};

Dropdown.propTypes = {
  onClose: PropTypes.func,
  defaultOpen: PropTypes.bool,
  enterDelay: PropTypes.number,
  closeOnSelfClick: PropTypes.bool,
  children: PropTypes.element.isRequired,
  trigger: PropTypes.oneOf(Object.values(DropdownTriggers)),
  placement: PropTypes.oneOf(Object.values(DropdownPlacements)),
  placeholderIcon: PropTypes.oneOfType([PropTypes.element,
    PropTypes.elementType, PropTypes.func, PropTypes.oneOf([null])]),
  ...DropdownContent.propTypes,
};

export {
  Dropdown as default,
  DropdownItem,
  DropdownPlacements,
  DropdownTriggers,
  DropdownTypes,
  DropdownSeparatorId,
};
